import styled from '@emotion/styled';
import { useCreation, useMemoizedFn, useRequest } from 'ahooks';
import { Form, FormProps, Input, Spin } from 'antd';
import { FormInstance } from 'antd/es/form';
import RoleCascader, { RoleCascaderProps, useRoleCascader } from 'bizComponents/RoleCascader';
import DecorateTitle from 'components/DecorateTitle';
import produce from 'immer';
import { cloneDeep, isNil, merge, sortBy } from 'lodash-es';
import { useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { getDepartmentDetail } from 'services/api/department/department';
import { scopesCannotChange } from 'services/api/department/permissions';
import { getRoleDetail } from 'services/api/department/roleWithDepartment';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import { RoleIdAccountsMap } from '../components/FormFields/AssignConversationConfigFields';
import PermissionsFields from '../components/FormFields/PermissionsFields';
import AssignConversationConfig from './AssignConversationConfig';
import RolePermScopeType from './RolePermScopeType';

interface DepartmentDetail extends StoreHomeV2.DepartmentDetail {}
interface RoleWithDepartment extends StoreHomeV2.RoleWithDepartment {}

const FormItem = Form.Item;
const { TextArea } = Input;
const NoResizeTextArea = styled(TextArea)`
  height: 80px;
  resize: none;
  textarea {
    resize: none;
  }
  ::after {
    position: relative;
    top: -25px;
    left: -10px;
  }
`;
const FieldTitle = styled(DecorateTitle)`
  margin-left: 16px;
`;

const ShortFormItem = styled(FormItem)`
  width: 500px;
`;

export interface RoleDetailFormValues
  extends Omit<RoleWithDepartment, 'appliedCount' | 'assignConversation'> {
  // permissionWithScopes: RoleWithDepartment['permissionWithScopes'];
  allScopes?: General.StoreScope[];
}
export interface RoleDetailFormProps extends FormProps<RoleDetailFormValues> {
  partialMode?: boolean; // 在部门管理 -> 切换角色 的场景下部分角色信息不用展示
  disabled?: boolean;
  department: DepartmentDetail;
  roleIdAccountsMap: RoleIdAccountsMap;
}

/**
 * 使用函数防止引用污染
 * @returns
 */
const defaultValues: () => Partial<RoleDetailFormValues> = () => ({
  // @ts-expect-error
  assignConversationConfig: {
    assignConversation: true,
    substituteType: 'NO_SUBSTITUTE',
    receptionScope: 'ALL_BUYER',
  },
  rolePermScopeType: 'DIFF_BY_PERM',
});
const patchUpDefaultPlatformStoreScopes = (
  values: Partial<RoleDetailFormValues>,
  defaultStoreScopes: DepartmentDetail['platformStoreScopes'],
) => {
  /**
   * 为某些默认没有添加StoreScope的数据使用当前部门的StoreScope进行补充
   * @param scopes
   * @returns
   */
  const dealWithPlatformStoreScope = (scopes?: General.StoreScope[]) => {
    if (!scopes || scopes.length === 0) {
      return cloneDeep(defaultStoreScopes);
    }
    /**
     * 满足条件的scopes可以跳过patch
     */
    const shouldNotPatch = scopes.some((r) => {
      const len = r?.regionStoreScopes?.length ?? 0;
      // regionStoreScopes 默认状态下只会选中1个， 大于 1 表示手动选择过，可以直接跳过
      if (len > 1) {
        return true;
      }
      // storeCount undefined 基本可以认为没有进行过手动选择，为 0 时通常表示没有选中过店铺，很可能是脏数据
      // 因此storeCount 大于 0 时 可以认为进行过处理或手动选择过，直接跳过
      let storeCount = r.storeCount || r.regionStoreScopes?.[0]?.storeCount;
      // 可能存在storeIds不为空但是storeCount为undefined的情况
      storeCount = storeCount || r.regionStoreScopes?.[0]?.storeIds?.length || 0;
      if (storeCount > 0) {
        return true;
      }
      return false;
    });
    if (shouldNotPatch) {
      return scopes;
    }
    return scopes.map((r) => {
      return cloneDeep(defaultStoreScopes.find((t) => t.platform === r.platform)) || r;
    });
  };
  const afterPatchPermission = produce(values, (draft: Partial<RoleDetailFormValues>) => {
    if (!draft) {
      return {};
    }
    const { permissionWithScopes = [] } = draft;

    if ((permissionWithScopes?.length ?? 0) < 1) {
      return draft;
    }
    // eslint-disable-next-line no-param-reassign
    draft.permissionWithScopes = permissionWithScopes.map((permission) => {
      if (permission?.platformStoreScopes?.length < 1) {
        return merge({ platformStoreScopes: cloneDeep(defaultStoreScopes) }, permission);
      }
      return {
        ...permission,
        platformStoreScopes: dealWithPlatformStoreScope(permission.platformStoreScopes),
      };
    });

    return draft;
  });
  const afterPathAssignConfig = produce(afterPatchPermission, (draft) => {
    // eslint-disable-next-line no-param-reassign
    draft.assignConversationConfig = {
      ...draft.assignConversationConfig!,
      assignConversation: draft?.assignConversationConfig?.assignConversation ?? false,
      platformStoreScopes: dealWithPlatformStoreScope(
        draft.assignConversationConfig?.platformStoreScopes,
      ),
    };
    return draft;
  });
  return afterPathAssignConfig;
};
const RoleDetailForm = (props: RoleDetailFormProps) => {
  const {
    disabled = false,
    department,
    onValuesChange,
    initialValues,
    roleIdAccountsMap,
    children = null,
    partialMode = false,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const formRef = useRef<FormInstance<any>>(null);
  const hasId = !isNil(initialValues?.id);
  const departmentId = department.id;
  const defaultStoreScopes = department?.platformStoreScopes ?? [];
  const mergedInitialValues = useCreation(() => {
    if (!initialValues) {
      return defaultValues();
    }
    const mergedInitialValues = merge(
      defaultValues(),
      patchUpDefaultPlatformStoreScopes(initialValues, defaultStoreScopes),
    );

    const { rolePermScopeType, permissionWithScopes } = mergedInitialValues;

    if (rolePermScopeType === 'DIFF_BY_PERM') {
      return mergedInitialValues;
    }

    const firstCanChangeScope = permissionWithScopes?.find(
      ({ permissionName }) => !scopesCannotChange.includes(permissionName),
    );

    return {
      ...mergedInitialValues,
      allScopes: firstCanChangeScope ? firstCanChangeScope.platformStoreScopes : [],
    };
  }, [initialValues, defaultStoreScopes]);

  const { runAsync: getRoleDetailAsync, loading: getRoleDetailLoading } = useRequest(
    (roleId: StoreHomeV2.RoleWithDepartment['id']) => {
      return getRoleDetail(roleId).then((r) => r.data.data);
    },
    { manual: true },
  );
  const handleCopyRoleChange = useMemoizedFn<Exclude<RoleCascaderProps['onChange'], undefined>>(
    async (v) => {
      if (!v) {
        return;
      }
      try {
        const { roleId, departmentId: selectedDeptId } = v;

        const roleDetail = await getRoleDetailAsync(roleId);

        const { rolePermScopeType, permissionWithScopes, assignConversationConfig } = roleDetail;

        const permisssions =
          selectedDeptId === departmentId
            ? permissionWithScopes
            : permissionWithScopes.map((p) => {
                return { ...p, platformStoreScopes: department.platformStoreScopes };
              });

        const conversationConfig =
          selectedDeptId === departmentId
            ? assignConversationConfig
            : {
                ...assignConversationConfig,
                platformStoreScopes: department.platformStoreScopes,
              };

        if (rolePermScopeType === 'DIFF_BY_PERM') {
          formRef.current?.setFieldsValue({
            permissionWithScopes: permisssions,
            assignConversationConfig: conversationConfig,
            rolePermScopeType,
          });
        } else {
          const firstCanChangeScope = permisssions.find(
            ({ permissionName }) => !scopesCannotChange.includes(permissionName),
          );

          formRef.current?.setFieldsValue({
            permissionWithScopes: permisssions,
            assignConversationConfig: conversationConfig,
            rolePermScopeType,
            allScopes: firstCanChangeScope ? firstCanChangeScope.platformStoreScopes : [],
          });
        }
      } catch (e) {
        errorToasterWithServerMsg(e, t('department.拉取角色数据失败，请稍后再试'));
      }
    },
  );
  const {
    request: { data: roleCascaderDataSource = [], loading },
  } = useRoleCascader({
    onSuccess: !isNil(initialValues?.id)
      ? undefined
      : (data) => {
          if (!data) {
            return;
          }
          type Role = StoreHomeV2.DepartmentDetail['roles'][number];
          let defaultAssistantRole: Role | null = null;
          let foundDeptId: StoreHomeV2.DepartmentDetail['id'] | null = null;
          let idx = 0;
          while (data[idx]) {
            const dept = data[idx];
            if (dept.isDefault || dept.id === departmentId) {
              const found =
                dept?.roles?.find((r) => {
                  return r.isDefaultAssistantRole;
                }) ?? null;
              defaultAssistantRole = found || defaultAssistantRole;
              foundDeptId = found ? dept.id : foundDeptId;
              // 如果找到本部门的 defaultAssistantRole, 优先使用本部门，
              // 如果本部门没有，使用默认部门，
              // 如果都没有，不处理
              if (found && dept.id === departmentId) {
                break;
              }
            }
            idx += 1;
          }
          if (initialValues?.copyRoleId || defaultAssistantRole) {
            formRef.current?.setFieldsValue?.({
              copyRoleId: initialValues?.copyRoleId ?? defaultAssistantRole?.id,
            });
            handleCopyRoleChange({
              roleId: initialValues?.copyRoleId ?? defaultAssistantRole?.id,
              departmentId: foundDeptId!,
            });
          }
        },
  });
  const validRoles = useCreation(() => {
    const skip = ['超级管理员'];

    return sortBy(
      roleCascaderDataSource.map((d) => {
        return {
          ...d,
          roles: sortBy(
            d.roles?.filter((r) => !skip.includes(r.name)),
            ['id', 'asc'],
          ),
        };
      }) ?? [],
      ['id', 'asc'],
    );
  }, [roleCascaderDataSource]);
  return (
    <Spin spinning={loading}>
      <Form<RoleDetailFormValues>
        initialValues={mergedInitialValues}
        ref={formRef}
        layout="vertical"
        {...otherProps}
      >
        {hasId ? <FormItem name="id" hidden /> : null}
        <ShortFormItem
          name="name"
          label={t('department.角色名称')}
          required
          validateTrigger="onSubmit"
          normalize={(v) => v?.trim?.() ?? ''}
          hidden={partialMode}
          rules={[
            {
              required: true,
              validator: async (_, value) => {
                if (!value) {
                  throw new Error(t('department.请输入x', { x: t('department.角色名称') }));
                }
                const roles = await getDepartmentDetail(departmentId)
                  .then((r) => r.roles || [])
                  .catch((_e) => {
                    return Promise.resolve([]);
                  });
                if (roles.find((r) => r.id !== initialValues?.id && r.name === value)) {
                  throw new Error(t('department.已存在相同名称的角色'));
                }
              },
            },
          ]}
        >
          <Input disabled={disabled} />
        </ShortFormItem>
        <ShortFormItem name="description" label={t('department.角色描述')} hidden={partialMode}>
          <NoResizeTextArea disabled={disabled} maxLength={200} showCount />
        </ShortFormItem>
        {disabled ? null : (
          <ShortFormItem
            hidden={partialMode}
            name="copyRoleId"
            normalize={(v) => v?.roleId}
            label={t('department.复制已有角色权限')}
          >
            <RoleCascader
              dataSource={validRoles}
              disabled={disabled}
              allowClear={false}
              onChange={handleCopyRoleChange}
            />
          </ShortFormItem>
        )}
        <section>
          <FieldTitle>{t('department.权限配置')}</FieldTitle>
          <FormItem hidden name="allScopes" />
          <FormItem hidden name="rolePermScopeType" />
          <RolePermScopeType
            departmentId={departmentId}
            departmentScopes={department?.platformStoreScopes}
            disabled={disabled}
          />
          <AssignConversationConfig
            departmentId={departmentId}
            disabled={disabled}
            roleId={initialValues?.id}
            departmentScopes={department?.platformStoreScopes}
            roleIdAccountsMap={roleIdAccountsMap}
          />
          <FormItem noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const permissionWithScopes =
                getFieldValue('permissionWithScopes') || initialValues?.permissionWithScopes || [];
              const assignBuyerAndOrderRule = getFieldValue([
                'assignConversationConfig',
                'assignBuyerAndOrderRule',
              ]);
              return (
                <PermissionsFields
                  // copyRole切换时需要刷新permissionWithScopes，
                  // 又因为PermissionsFields使用initialValue构造某些关键ui（权限依赖permissionsDeps）相关的变量
                  // 而为了使PermissionsFields 内能够响应 initialValue的变动，因此使用key强制replace该节点
                  // 本来可以通过dependencies copyRoleId，
                  // 但是因为 permissionWithScopes 需要通过request异步获取
                  // copyRoleId变动时，真正的permissionWithScopes仍在pending，因此需要关联request状态
                  // loading可以比较确保 setFileds permissionWithScopes 的部分已执行，所以，使用loading状态进行关联
                  key={`PermissionsFields_with_copyRoleId_${getFieldValue('copyRoleId') ?? -1}_${
                    getRoleDetailLoading ? 1 : 0
                  }`}
                  initialValue={permissionWithScopes}
                  departmentScopes={department?.platformStoreScopes}
                  departmentId={departmentId}
                  assignBuyerAndOrderRule={assignBuyerAndOrderRule}
                  disabled={disabled}
                  label={t('department.选择角色权限')}
                  name="permissionWithScopes"
                  required
                  //  getRoleDetailLoading 为true时表示数据仍在加载，可以跳过更新，减少rerender
                  shouldUpdate={!getRoleDetailLoading}
                  dependencies={['copyRoleId']}
                  rules={[
                    {
                      validator: (_, v) => {
                        if (v && v.length > 0) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('department.请配置权限')));
                      },
                    },
                  ]}
                />
              );
            }}
          </FormItem>
        </section>
        {children}
      </Form>
    </Spin>
  );
};
export default RoleDetailForm;
